import React, { useState, useEffect } from 'react'
import { ensureSessionHeaders } from '../../lib/serverSession'
import { logAdminAction } from '../../lib/adminAuth'
import { validateKYCForFinancialOperations } from '../../lib/kycValidation'
import { withdrawalEscrowService } from '../../lib/services/withdrawalEscrowService'
import RecordExporter from './RecordExporter'
import { filterAndRank } from '../../lib/searchRelevance'

interface User {
  id: number
  username: string
  full_name: string | null
  email: string
}

interface CommissionWithdrawalManagerProps {
  currentUser?: any
}

const JP_MANUAL_WITHDRAWAL_OPERATOR_USER_ID = 106

function getCurrentNumericUserId(currentUser: any): number | null {
  const candidates = [
    currentUser?.database_user?.id,
    currentUser?.user?.id,
    currentUser?.id,
    currentUser?.user_id,
    currentUser?.adminUser?.user_id
  ]
  for (const candidate of candidates) {
    const parsed = Number(candidate)
    if (Number.isFinite(parsed)) return parsed
  }
  return null
}

export const CommissionWithdrawalManager: React.FC<CommissionWithdrawalManagerProps> = ({
  currentUser
}) => {
  const [activeTab, setActiveTab] = useState<'commission' | 'withdrawal' | 'requests' | 'audit'>('requests')
  const currentNumericUserId = getCurrentNumericUserId(currentUser)
  const canUseManualWithdrawal = currentNumericUserId === JP_MANUAL_WITHDRAWAL_OPERATOR_USER_ID &&
    currentUser?.adminUser?.role === 'super_admin' &&
    currentUser?.adminUser?.is_active !== false
  const [loading, setLoading] = useState(false)
  const [users, setUsers] = useState<User[]>([])
  const [searchTerm, setSearchTerm] = useState('')
  const [recentRecords, setRecentRecords] = useState<any[]>([])

  // Withdrawal requests management
  const [pendingWithdrawals, setPendingWithdrawals] = useState<any[]>([])
  const [selectedWithdrawal, setSelectedWithdrawal] = useState<any>(null)
  const [showApprovalModal, setShowApprovalModal] = useState(false)
  const [showRejectionModal, setShowRejectionModal] = useState(false)
  const [proofFile, setProofFile] = useState<File | null>(null)
  const [transactionHash, setTransactionHash] = useState('')
  const [rejectionReason, setRejectionReason] = useState('')
  const [processingAction, setProcessingAction] = useState(false)

  // Audit trail
  const [auditRecords, setAuditRecords] = useState<any[]>([])

  // Commission form states
  const [commissionForm, setCommissionForm] = useState({
    userId: '',
    type: 'referral',
    amount: '',
    description: '',
    date: new Date().toISOString().split('T')[0],
    password: ''
  })

  // Withdrawal form states
  const [withdrawalForm, setWithdrawalForm] = useState({
    userId: '',
    amount: '',
    userWalletAddress: '', // User's wallet address (where they want to receive)
    companyWalletAddress: '', // Company wallet address (where we send from)
    transactionHash: '',
    proofOfPayment: '', // URL or file reference for proof
    method: 'crypto',
    network: 'USDT-BEP20',
    status: 'pending',
    requestDate: new Date().toISOString().split('T')[0],
    processedDate: '',
    adminNotes: ''
  })

  const [showConfirmDialog, setShowConfirmDialog] = useState<{
    show: boolean
    type: 'commission' | 'withdrawal'
    data?: any
  }>({ show: false, type: 'commission' })

  // v4.7.71: step-up TOTP for create_commission_adjustment. Held only while
  // the confirm dialog is open; cleared on close / success.
  const [commissionTotpCode, setCommissionTotpCode] = useState('')
  const [commissionMfaError, setCommissionMfaError] = useState<string | null>(null)

  const [showExporter, setShowExporter] = useState<{
    show: boolean
    type: 'commission' | 'withdrawal'
  }>({ show: false, type: 'commission' })

  const [uploadingProof, setUploadingProof] = useState(false)

  useEffect(() => {
    if (activeTab === 'withdrawal' && !canUseManualWithdrawal) {
      setActiveTab('requests')
      return
    }
    loadUsers()
    loadRecentRecords()
    if (activeTab === 'requests') {
      loadPendingWithdrawals()
    } else if (activeTab === 'audit') {
      loadAuditRecords()
    }
  }, [activeTab, canUseManualWithdrawal])

  const loadUsers = async () => {
    try {
      const sessionHeaders = await ensureSessionHeaders()
      const response = await fetch('/api/admin/commission-operations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...sessionHeaders
        },
        body: JSON.stringify({ operation: 'load_users_paginated' })
      })
      const result = await response.json()
      if (!response.ok) throw new Error(result.error || 'Failed to load users')
      setUsers(result.data || [])
    } catch (error) {
      console.error('Error loading users:', error)
    }
  }

  const loadRecentRecords = async () => {
    try {
      const sessionHeaders = await ensureSessionHeaders()
      const operation = activeTab === 'commission' ? 'load_recent_commission_records' : 'load_recent_withdrawal_records'
      const response = await fetch('/api/admin/commission-operations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...sessionHeaders
        },
        body: JSON.stringify({ operation, data: { limit: 10 } })
      })
      const result = await response.json()
      if (!response.ok) throw new Error(result.error || 'Failed to load records')
      setRecentRecords(result.data || [])
    } catch (error) {
      console.error('Error loading recent records:', error)
    }
  }

  const loadPendingWithdrawals = async () => {
    try {
      const data = await withdrawalEscrowService.getPendingWithdrawals()
      setPendingWithdrawals(data || [])
    } catch (error) {
      console.error('Error loading pending withdrawals:', error)
    }
  }

  const loadAuditRecords = async () => {
    try {
      const sessionHeaders = await ensureSessionHeaders()
      const response = await fetch('/api/admin/commission-operations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...sessionHeaders
        },
        body: JSON.stringify({ operation: 'load_audit_records', data: { limit: 50 } })
      })
      const result = await response.json()
      if (!response.ok) throw new Error(result.error || 'Failed to load audit records')
      setAuditRecords(result.data || [])
    } catch (error) {
      console.error('Error loading audit records:', error)
      setAuditRecords([])
    }
  }

  const handleApproveWithdrawal = async () => {
    if (!selectedWithdrawal || !proofFile || !transactionHash.trim()) {
      alert('Please provide proof of payment and transaction hash')
      return
    }

    setProcessingAction(true)
    try {
      // Upload proof of payment via API
      const fileExt = proofFile.name.split('.').pop()
      const fileName = `withdrawal-proof-${selectedWithdrawal.id}.${fileExt}`

      // Convert file to base64
      const fileBase64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => {
          const base64 = (reader.result as string).split(',')[1]
          resolve(base64)
        }
        reader.onerror = reject
        reader.readAsDataURL(proofFile)
      })

      const sessionHeaders = await ensureSessionHeaders()
      const uploadResponse = await fetch('/api/admin/withdrawals', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...sessionHeaders
        },
        body: JSON.stringify({
          operation: 'upload_proof',
          data: { withdrawalId: selectedWithdrawal.id, fileBase64, fileName, contentType: proofFile.type }
        })
      })
      const uploadResult = await uploadResponse.json()
      if (!uploadResponse.ok) throw new Error(uploadResult.error || 'Failed to upload proof')

      // Approve withdrawal — require a valid admin ID
      const adminId = currentUser?.adminUser?.id
      if (!adminId) {
        alert('Error: Could not determine admin identity. Please log in again.')
        setProcessingAction(false)
        return
      }

      const result = await withdrawalEscrowService.approveWithdrawal(
        selectedWithdrawal.id,
        adminId,
        uploadResult.data.storagePath,
        transactionHash,
        'Approved by admin'
      )

      if (result.success) {
        alert('Withdrawal approved successfully!')
        setShowApprovalModal(false)
        setSelectedWithdrawal(null)
        setProofFile(null)
        setTransactionHash('')
        loadPendingWithdrawals()
      } else {
        alert(`Failed to approve withdrawal: ${result.error}`)
      }
    } catch (error) {
      console.error('Error approving withdrawal:', error)
      alert('Error approving withdrawal. Please try again.')
    } finally {
      setProcessingAction(false)
    }
  }

  const handleRejectWithdrawal = async () => {
    if (!selectedWithdrawal || !rejectionReason.trim()) {
      alert('Please provide a rejection reason')
      return
    }

    setProcessingAction(true)
    try {
      const result = await withdrawalEscrowService.rejectWithdrawal(
        selectedWithdrawal.id,
        currentUser?.adminUser?.id || 1,
        rejectionReason
      )

      if (result.success) {
        alert('Withdrawal rejected successfully!')
        setShowRejectionModal(false)
        setSelectedWithdrawal(null)
        setRejectionReason('')
        loadPendingWithdrawals()
      } else {
        alert(`Failed to reject withdrawal: ${result.error}`)
      }
    } catch (error) {
      console.error('Error rejecting withdrawal:', error)
      alert('Error rejecting withdrawal. Please try again.')
    } finally {
      setProcessingAction(false)
    }
  }

  const filteredUsers = filterAndRank(users, searchTerm, (user) => ({
    primary: [user.username, user.email, user.id?.toString()],
  }))

  const handleCommissionSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!commissionForm.userId || !commissionForm.amount || !commissionForm.password) {
      alert('Please fill in all required fields including the master admin password')
      return
    }

    setShowConfirmDialog({
      show: true,
      type: 'commission',
      data: commissionForm
    })
  }

  const handleProofUpload = async (file: File) => {
    if (!file) return null
    if (!canUseManualWithdrawal) {
      alert('Manual withdrawal proof upload is restricted to the approved super admin operator.')
      return null
    }

    setUploadingProof(true)
    try {
      // Create a unique filename
      const fileExt = file.name.split('.').pop()
      const fileName = `withdrawal_proof_${Date.now()}.${fileExt}`

      // Convert file to base64
      const fileBase64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => {
          const base64 = (reader.result as string).split(',')[1]
          resolve(base64)
        }
        reader.onerror = reject
        reader.readAsDataURL(file)
      })

      // Upload via API
      const sessionHeaders = await ensureSessionHeaders()
      const response = await fetch('/api/admin/commission-operations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...sessionHeaders
        },
        body: JSON.stringify({
          operation: 'upload_withdrawal_proof',
          data: { fileBase64, fileName, contentType: file.type }
        })
      })
      const result = await response.json()
      if (!response.ok) {
        console.error('Upload error:', result.error)
        alert('Failed to upload proof of payment. Please try again.')
        return null
      }

      return result.data.publicUrl
    } catch (error) {
      console.error('Upload error:', error)
      alert('Failed to upload proof of payment. Please try again.')
      return null
    } finally {
      setUploadingProof(false)
    }
  }

  const handleWithdrawalSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!withdrawalForm.userId || !withdrawalForm.amount || !withdrawalForm.userWalletAddress) {
      alert('Please fill in all required fields')
      return
    }

    setShowConfirmDialog({
      show: true,
      type: 'withdrawal',
      data: withdrawalForm
    })
  }

  const confirmAction = async () => {
    // v4.7.71: enforce TOTP digit-shape on the client before round-tripping.
    if (showConfirmDialog.type === 'commission') {
      const t = (commissionTotpCode || '').trim()
      if (!/^\d{6}$/.test(t) && !/^\d{8}$/.test(t)) {
        setCommissionMfaError('Enter your 6-digit authenticator code (or 8-digit backup code).')
        return
      }
      setCommissionMfaError(null)
    }
    setLoading(true)
    try {
      if (showConfirmDialog.type === 'commission') {
        await processCommission()
      } else {
        await processWithdrawal()
      }

      setShowConfirmDialog({ show: false, type: 'commission' })
      setCommissionTotpCode('')
      setCommissionMfaError(null)
      loadRecentRecords()
    } catch (error: any) {
      console.error('Error processing action:', error)
      // v4.7.71: surface MFA-specific errors inline in the dialog instead of
      // alert()-ing and losing dialog state. Keep the dialog open so the admin
      // can re-enter the TOTP without re-typing the form.
      const code = error?.code
      if (code === 'MFA_TOTP_REQUIRED' || code === 'MFA_INVALID' || code === 'MFA_LOCKED') {
        setCommissionMfaError(error?.message || 'Authenticator verification failed. Please try again.')
        setCommissionTotpCode('')
      } else {
        alert(error?.message || 'Error processing request. Please try again.')
        // Always clear password on non-MFA failure for security
        setCommissionForm(prev => ({ ...prev, password: '' }))
      }
    } finally {
      setLoading(false)
    }
  }

  const processCommission = async () => {
    const amount = parseFloat(commissionForm.amount)
    const userId = parseInt(commissionForm.userId)

    // Create commission via API
    const sessionHeaders = await ensureSessionHeaders()
    const response = await fetch('/api/admin/commission-operations', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...sessionHeaders
      },
      body: JSON.stringify({
        operation: 'create_commission_adjustment',
        data: {
          userId,
          amount,
          type: commissionForm.type,
          description: commissionForm.description,
          date: commissionForm.date,
          adminId: currentUser?.adminUser?.id || null,
          password: commissionForm.password,
          // v4.7.71: required step-up TOTP token (6-digit authenticator or
          // 8-digit backup code). Server returns MFA_TOTP_REQUIRED / MFA_INVALID
          // / MFA_LOCKED on failure; confirmAction routes those to inline UI.
          totpToken: (commissionTotpCode || '').trim()
        }
      })
    })
    const result = await response.json()
    if (!response.ok) {
      // v4.7.71: preserve the server error code so confirmAction can render
      // MFA failures inline instead of as a generic alert().
      const err: any = new Error(result.error || 'Failed to create commission')
      if (result.code) err.code = result.code
      throw err
    }

    // Email notification is handled automatically by the Postgres trigger on commission_transactions
    // (notify_new_commission) → email_notifications queue → cron/process-email-queue.js

    // Log admin action
    await logAdminAction(
      currentUser?.adminUser?.id || 'unknown',
      'commission_adjustment',
      `Added ${amount} USDT commission to user ${userId}`,
      JSON.stringify({ userId, amount, type: commissionForm.type, description: commissionForm.description })
    )

    // Reset form (always clear password)
    setCommissionForm({
      userId: '',
      type: 'referral',
      amount: '',
      description: '',
      date: new Date().toISOString().split('T')[0],
      password: ''
    })

    alert('Commission added successfully!')
  }

  const processWithdrawal = async () => {
    if (!canUseManualWithdrawal) {
      alert('Manual withdrawal record creation is restricted to the approved super admin operator.')
      return
    }

    const amount = parseFloat(withdrawalForm.amount)
    const userId = parseInt(withdrawalForm.userId)

    // Validate KYC status before processing withdrawal
    if (withdrawalForm.status === 'completed') {
      const kycValidation = await validateKYCForFinancialOperations(userId)
      if (!kycValidation.canWithdraw) {
        alert(`Cannot process withdrawal: ${kycValidation.message}`)
        return
      }
    }

    // Create withdrawal record via API
    const sessionHeaders = await ensureSessionHeaders()
    const response = await fetch('/api/admin/commission-operations', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...sessionHeaders
      },
      body: JSON.stringify({
        operation: 'create_withdrawal_record',
        data: {
          userId,
          amount,
          walletAddress: withdrawalForm.userWalletAddress,
          companyWalletAddress: withdrawalForm.companyWalletAddress,
          transactionHash: withdrawalForm.transactionHash,
          proofOfPayment: withdrawalForm.proofOfPayment,
          method: withdrawalForm.method,
          network: withdrawalForm.network,
          status: withdrawalForm.status,
          requestDate: withdrawalForm.requestDate,
          processedDate: withdrawalForm.processedDate,
          adminNotes: withdrawalForm.adminNotes,
          adminId: currentUser?.adminUser?.id || null,
          requireKycValidation: withdrawalForm.status === 'completed'
        }
      })
    })
    const result = await response.json()
    if (!response.ok) throw new Error(result.error || 'Failed to create withdrawal')

    // Log admin action
    await logAdminAction(
      currentUser?.adminUser?.id || 'unknown',
      'withdrawal_created',
      `Created withdrawal record for user ${userId}: ${amount} USDT`,
      JSON.stringify({
        userId,
        amount,
        status: withdrawalForm.status,
        userWalletAddress: withdrawalForm.userWalletAddress,
        companyWalletAddress: withdrawalForm.companyWalletAddress,
        transactionHash: withdrawalForm.transactionHash
      })
    )

    // Reset form
    setWithdrawalForm({
      userId: '',
      amount: '',
      userWalletAddress: '',
      companyWalletAddress: '',
      transactionHash: '',
      proofOfPayment: '',
      method: 'crypto',
      network: 'USDT-BEP20',
      status: 'pending',
      requestDate: new Date().toISOString().split('T')[0],
      processedDate: '',
      adminNotes: ''
    })

    alert('Withdrawal record created successfully!')
  }

  return (
    <div style={{
      backgroundColor: '#0F0F0F',
      minHeight: '100vh',
      padding: '24px',
      color: '#FFFFFF'
    }}>
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto'
      }}>
        {/* Header */}
        <div style={{ marginBottom: '32px' }}>
          <h1 style={{
            fontSize: '28px',
            fontWeight: '700',
            color: '#F59E0B',
            marginBottom: '8px'
          }}>
            💰 Commission & Withdrawal Management
          </h1>
          <p style={{ color: '#ffffff', fontSize: '16px' }}>
            Manage user commissions and withdrawal requests
          </p>
        </div>

        {/* Tab Navigation */}
        <div style={{
          display: 'flex',
          marginBottom: '32px',
          borderBottom: '1px solid #1F2937'
        }}>
          {[
            { id: 'requests', name: 'Withdrawal Requests', icon: '📋' },
            { id: 'audit', name: 'Audit Trail', icon: '📊' },
            { id: 'commission', name: 'Add Commission', icon: '💸' },
            ...(canUseManualWithdrawal ? [{ id: 'withdrawal', name: 'Create Withdrawal', icon: '🏦' }] : [])
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              style={{
                padding: '12px 24px',
                backgroundColor: activeTab === tab.id ? '#1F2937' : 'transparent',
                color: activeTab === tab.id ? '#F59E0B' : '#9CA3AF',
                border: 'none',
                borderBottom: activeTab === tab.id ? '2px solid #F59E0B' : '2px solid transparent',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: '600',
                transition: 'all 0.2s ease'
              }}
            >
              {tab.icon} {tab.name}
            </button>
          ))}
        </div>

        {/* Main Content */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 400px',
          gap: '32px'
        }}>
          {/* Form Section */}
          <div style={{
            backgroundColor: '#1F2937',
            borderRadius: '12px',
            padding: '24px',
            border: '1px solid #374151'
          }}>
            {activeTab === 'requests' ? (
              <div>
                <h2 style={{
                  fontSize: '20px',
                  fontWeight: '600',
                  color: '#F59E0B',
                  marginBottom: '24px'
                }}>
                  📋 Withdrawal Requests Management
                </h2>

                {/* Pending Withdrawals Table */}
                <div style={{
                  backgroundColor: '#111827',
                  borderRadius: '8px',
                  padding: '20px',
                  marginBottom: '24px',
                  border: '1px solid #374151'
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '16px'
                  }}>
                    <h3 style={{
                      fontSize: '16px',
                      fontWeight: '600',
                      color: '#ffffff',
                      margin: 0
                    }}>
                      Pending Withdrawal Requests ({pendingWithdrawals.length})
                    </h3>
                    <button
                      onClick={loadPendingWithdrawals}
                      style={{
                        padding: '8px 16px',
                        backgroundColor: '#374151',
                        color: '#ffffff',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}
                    >
                      🔄 Refresh
                    </button>
                  </div>

                  {pendingWithdrawals.length === 0 ? (
                    <div style={{
                      textAlign: 'center',
                      padding: '40px',
                      color: '#ffffff'
                    }}>
                      <div style={{ fontSize: '48px', marginBottom: '16px' }}>📭</div>
                      <p>No pending withdrawal requests</p>
                    </div>
                  ) : (
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{
                        width: '100%',
                        borderCollapse: 'collapse',
                        fontSize: '14px'
                      }}>
                        <thead>
                          <tr style={{ borderBottom: '1px solid #374151' }}>
                            <th style={{ padding: '12px', textAlign: 'left', color: '#ffffff', fontWeight: '500' }}>User</th>
                            <th style={{ padding: '12px', textAlign: 'left', color: '#ffffff', fontWeight: '500' }}>Amount</th>
                            <th style={{ padding: '12px', textAlign: 'left', color: '#ffffff', fontWeight: '500' }}>Wallet</th>
                            <th style={{ padding: '12px', textAlign: 'left', color: '#ffffff', fontWeight: '500' }}>Network</th>
                            <th style={{ padding: '12px', textAlign: 'left', color: '#ffffff', fontWeight: '500' }}>Date</th>
                            <th style={{ padding: '12px', textAlign: 'left', color: '#ffffff', fontWeight: '500' }}>Status</th>
                            <th style={{ padding: '12px', textAlign: 'center', color: '#ffffff', fontWeight: '500' }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {pendingWithdrawals.map((withdrawal) => (
                            <tr key={withdrawal.id} style={{ borderBottom: '1px solid #374151' }}>
                              <td style={{ padding: '12px', color: '#ffffff' }}>
                                <div>
                                  <div style={{ fontWeight: '500' }}>{withdrawal.users?.username || 'Unknown'}</div>
                                  <div style={{ fontSize: '14px', color: '#ffffff' }}>ID: {withdrawal.user_id}</div>
                                </div>
                              </td>
                              <td style={{ padding: '12px', color: '#F59E0B', fontWeight: '600' }}>
                                ${withdrawal.withdrawal_amount?.toFixed(2)}
                              </td>
                              <td style={{ padding: '12px', color: '#ffffff', fontFamily: 'monospace', fontSize: '14px' }}>
                                {withdrawal.wallet_address?.substring(0, 10)}...{withdrawal.wallet_address?.substring(-6)}
                              </td>
                              <td style={{ padding: '12px', color: '#ffffff' }}>
                                {withdrawal.network}
                              </td>
                              <td style={{ padding: '12px', color: '#ffffff', fontSize: '14px' }}>
                                {new Date(withdrawal.created_at).toLocaleDateString()}
                              </td>
                              <td style={{ padding: '12px' }}>
                                <span style={{
                                  padding: '4px 8px',
                                  borderRadius: '4px',
                                  fontSize: '14px',
                                  fontWeight: '500',
                                  backgroundColor:
                                    withdrawal.status === 'pending_verification' ? '#DBEAFE' :
                                    withdrawal.status === 'pending' ? '#FEF3C7' : '#D1FAE5',
                                  color:
                                    withdrawal.status === 'pending_verification' ? '#1E40AF' :
                                    withdrawal.status === 'pending' ? '#92400E' : '#065F46'
                                }}>
                                  {withdrawal.status === 'pending_verification' ? '⏳ AWAITING VERIFICATION' : withdrawal.status?.toUpperCase()}
                                </span>
                              </td>
                              <td style={{ padding: '12px', textAlign: 'center' }}>
                                {withdrawal.status === 'pending' && (
                                  <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                                    <button
                                      onClick={() => {
                                        setSelectedWithdrawal(withdrawal)
                                        setShowApprovalModal(true)
                                      }}
                                      style={{
                                        padding: '6px 12px',
                                        backgroundColor: '#10B981',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: 'pointer',
                                        fontSize: '14px'
                                      }}
                                    >
                                      ✅ Approve
                                    </button>
                                    <button
                                      onClick={() => {
                                        setSelectedWithdrawal(withdrawal)
                                        setShowRejectionModal(true)
                                      }}
                                      style={{
                                        padding: '6px 12px',
                                        backgroundColor: '#EF4444',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: 'pointer',
                                        fontSize: '14px'
                                      }}
                                    >
                                      ❌ Reject
                                    </button>
                                  </div>
                                )}
                                {withdrawal.status === 'processing' && (
                                  <span style={{ color: '#F59E0B', fontSize: '14px' }}>⏳ Awaiting User Confirmation</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            ) : activeTab === 'audit' ? (
              <div>
                <h2 style={{
                  fontSize: '20px',
                  fontWeight: '600',
                  color: '#F59E0B',
                  marginBottom: '24px'
                }}>
                  📊 Withdrawal Audit Trail
                </h2>

                {/* Audit Records Table */}
                <div style={{
                  backgroundColor: '#111827',
                  borderRadius: '8px',
                  padding: '20px',
                  marginBottom: '24px',
                  border: '1px solid #374151'
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '16px'
                  }}>
                    <h3 style={{
                      fontSize: '16px',
                      fontWeight: '600',
                      color: '#ffffff',
                      margin: 0
                    }}>
                      Recent Audit Records ({auditRecords.length})
                    </h3>
                    <button
                      onClick={loadAuditRecords}
                      style={{
                        padding: '8px 16px',
                        backgroundColor: '#374151',
                        color: '#ffffff',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}
                    >
                      🔄 Refresh
                    </button>
                  </div>

                  {auditRecords.length === 0 ? (
                    <div style={{
                      textAlign: 'center',
                      padding: '40px',
                      color: '#ffffff'
                    }}>
                      <div style={{ fontSize: '48px', marginBottom: '16px' }}>📋</div>
                      <p>No audit records found</p>
                    </div>
                  ) : (
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{
                        width: '100%',
                        borderCollapse: 'collapse',
                        fontSize: '14px'
                      }}>
                        <thead>
                          <tr style={{ borderBottom: '1px solid #374151' }}>
                            <th style={{ padding: '12px', textAlign: 'left', color: '#ffffff', fontWeight: '500' }}>Date/Time</th>
                            <th style={{ padding: '12px', textAlign: 'left', color: '#ffffff', fontWeight: '500' }}>User</th>
                            <th style={{ padding: '12px', textAlign: 'left', color: '#ffffff', fontWeight: '500' }}>Action</th>
                            <th style={{ padding: '12px', textAlign: 'left', color: '#ffffff', fontWeight: '500' }}>Status Change</th>
                            <th style={{ padding: '12px', textAlign: 'left', color: '#ffffff', fontWeight: '500' }}>Amount</th>
                            <th style={{ padding: '12px', textAlign: 'left', color: '#ffffff', fontWeight: '500' }}>Admin</th>
                            <th style={{ padding: '12px', textAlign: 'left', color: '#ffffff', fontWeight: '500' }}>Notes</th>
                          </tr>
                        </thead>
                        <tbody>
                          {auditRecords.map((record) => (
                            <tr key={record.id} style={{ borderBottom: '1px solid #374151' }}>
                              <td style={{ padding: '12px', color: '#ffffff', fontSize: '14px' }}>
                                <div>{new Date(record.created_at).toLocaleDateString()}</div>
                                <div style={{ color: '#ffffff' }}>{new Date(record.created_at).toLocaleTimeString()}</div>
                              </td>
                              <td style={{ padding: '12px', color: '#ffffff' }}>
                                <div style={{ fontWeight: '500' }}>{record.user?.username || 'Unknown'}</div>
                                <div style={{ fontSize: '14px', color: '#ffffff' }}>ID: {record.user_id}</div>
                              </td>
                              <td style={{ padding: '12px' }}>
                                <span style={{
                                  padding: '4px 8px',
                                  borderRadius: '4px',
                                  fontSize: '14px',
                                  fontWeight: '500',
                                  backgroundColor:
                                    record.action_type === 'requested' ? '#FEF3C7' :
                                    record.action_type === 'approved' ? '#D1FAE5' :
                                    record.action_type === 'rejected' ? '#FEE2E2' :
                                    record.action_type === 'confirmed' ? '#DBEAFE' :
                                    '#ffffff',
                                  color:
                                    record.action_type === 'requested' ? '#92400E' :
                                    record.action_type === 'approved' ? '#065F46' :
                                    record.action_type === 'rejected' ? '#991B1B' :
                                    record.action_type === 'confirmed' ? '#1E40AF' :
                                    '#374151'
                                }}>
                                  {record.action_type?.toUpperCase()}
                                </span>
                              </td>
                              <td style={{ padding: '12px', color: '#ffffff', fontSize: '14px' }}>
                                {record.old_status && (
                                  <span style={{ color: '#ffffff' }}>{record.old_status} → </span>
                                )}
                                <span style={{ color: '#F59E0B' }}>{record.new_status}</span>
                              </td>
                              <td style={{ padding: '12px', color: '#F59E0B', fontWeight: '600' }}>
                                ${record.amount?.toFixed(2)}
                              </td>
                              <td style={{ padding: '12px', color: '#ffffff', fontSize: '14px' }}>
                                {record.admin ? (
                                  <div>
                                    <div>{record.admin.username}</div>
                                    <div style={{ color: '#ffffff' }}>ID: {record.admin_id}</div>
                                  </div>
                                ) : (
                                  <span style={{ color: '#ffffff' }}>System</span>
                                )}
                              </td>
                              <td style={{ padding: '12px', color: '#ffffff', fontSize: '14px', maxWidth: '200px' }}>
                                {record.admin_notes || record.user_notes || '-'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            ) : activeTab === 'commission' ? (
              <div>
                <h2 style={{
                  fontSize: '20px',
                  fontWeight: '600',
                  color: '#F59E0B',
                  marginBottom: '24px'
                }}>
                  💸 Add Commission
                </h2>

                <form onSubmit={handleCommissionSubmit}>
                  {/* User Selection */}
                  <div style={{ marginBottom: '20px' }}>
                    <label style={{
                      display: 'block',
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#E5E7EB',
                      marginBottom: '8px'
                    }}>
                      Select User *
                    </label>
                    <input
                      type="text"
                      placeholder="Search by username, email, or ID..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '12px',
                        backgroundColor: '#374151',
                        border: '1px solid #4B5563',
                        borderRadius: '8px',
                        color: '#FFFFFF',
                        fontSize: '14px',
                        marginBottom: '8px'
                      }}
                    />
                    <select
                      value={commissionForm.userId}
                      onChange={(e) => setCommissionForm({ ...commissionForm, userId: e.target.value })}
                      required
                      style={{
                        width: '100%',
                        padding: '12px',
                        backgroundColor: '#374151',
                        border: '1px solid #4B5563',
                        borderRadius: '8px',
                        color: '#FFFFFF',
                        fontSize: '14px'
                      }}
                    >
                      <option value="">Select a user...</option>
                      {filteredUsers.map((user) => (
                        <option key={user.id} value={user.id}>
                          {user.username} - {user.full_name} ({user.email})
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Commission Type */}
                  <div style={{ marginBottom: '20px' }}>
                    <label style={{
                      display: 'block',
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#E5E7EB',
                      marginBottom: '8px'
                    }}>
                      Commission Type *
                    </label>
                    <select
                      value={commissionForm.type}
                      onChange={(e) => setCommissionForm({ ...commissionForm, type: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '12px',
                        backgroundColor: '#374151',
                        border: '1px solid #4B5563',
                        borderRadius: '8px',
                        color: '#FFFFFF',
                        fontSize: '14px'
                      }}
                    >
                      <option value="referral">Referral Commission</option>
                      <option value="bonus">Performance Bonus</option>
                      <option value="manual_adjustment">Manual Adjustment</option>
                      <option value="correction">Balance Correction</option>
                      <option value="incentive">Special Incentive</option>
                    </select>
                  </div>

                  {/* Amount */}
                  <div style={{ marginBottom: '20px' }}>
                    <label style={{
                      display: 'block',
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#E5E7EB',
                      marginBottom: '8px'
                    }}>
                      Amount (USDT) *
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={commissionForm.amount}
                      onChange={(e) => setCommissionForm({ ...commissionForm, amount: e.target.value })}
                      required
                      style={{
                        width: '100%',
                        padding: '12px',
                        backgroundColor: '#374151',
                        border: '1px solid #4B5563',
                        borderRadius: '8px',
                        color: '#FFFFFF',
                        fontSize: '14px'
                      }}
                      placeholder="0.00"
                    />
                  </div>

                  {/* Description */}
                  <div style={{ marginBottom: '20px' }}>
                    <label style={{
                      display: 'block',
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#E5E7EB',
                      marginBottom: '8px'
                    }}>
                      Description/Notes *
                    </label>
                    <textarea
                      value={commissionForm.description}
                      onChange={(e) => setCommissionForm({ ...commissionForm, description: e.target.value })}
                      required
                      rows={3}
                      style={{
                        width: '100%',
                        padding: '12px',
                        backgroundColor: '#374151',
                        border: '1px solid #4B5563',
                        borderRadius: '8px',
                        color: '#FFFFFF',
                        fontSize: '14px',
                        resize: 'vertical'
                      }}
                      placeholder="Reason for commission adjustment..."
                    />
                  </div>

                  {/* Master Admin Password */}
                  <div style={{ marginBottom: '20px' }}>
                    <label style={{
                      display: 'block',
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#E5E7EB',
                      marginBottom: '8px'
                    }}>
                      🔐 Master Admin Password *
                    </label>
                    <input
                      type="password"
                      value={commissionForm.password}
                      onChange={(e) => setCommissionForm({ ...commissionForm, password: e.target.value })}
                      required
                      autoComplete="off"
                      style={{
                        width: '100%',
                        padding: '12px',
                        backgroundColor: '#374151',
                        border: '1px solid #EF4444',
                        borderRadius: '8px',
                        color: '#FFFFFF',
                        fontSize: '14px'
                      }}
                      placeholder="Enter master admin password to authorize"
                    />
                    <p style={{ fontSize: '12px', color: '#9CA3AF', marginTop: '4px' }}>
                      Required for security verification. Password is never stored.
                    </p>
                  </div>

                  {/* Date */}
                  <div style={{ marginBottom: '24px' }}>
                    <label style={{
                      display: 'block',
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#E5E7EB',
                      marginBottom: '8px'
                    }}>
                      Commission Date *
                    </label>
                    <input
                      type="date"
                      value={commissionForm.date}
                      onChange={(e) => setCommissionForm({ ...commissionForm, date: e.target.value })}
                      required
                      style={{
                        width: '100%',
                        padding: '12px',
                        backgroundColor: '#374151',
                        border: '1px solid #4B5563',
                        borderRadius: '8px',
                        color: '#FFFFFF',
                        fontSize: '14px'
                      }}
                    />
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={loading}
                    style={{
                      width: '100%',
                      padding: '14px',
                      backgroundColor: '#F59E0B',
                      color: '#000000',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '16px',
                      fontWeight: '600',
                      cursor: loading ? 'not-allowed' : 'pointer',
                      opacity: loading ? 0.7 : 1,
                      transition: 'all 0.2s ease'
                    }}
                  >
                    {loading ? 'Processing...' : '💸 Add Commission'}
                  </button>
                </form>
              </div>
            ) : (
              <div>
                <h2 style={{
                  fontSize: '20px',
                  fontWeight: '600',
                  color: '#F59E0B',
                  marginBottom: '24px'
                }}>
                  🏦 Create Withdrawal Record
                </h2>

                <form onSubmit={handleWithdrawalSubmit}>
                  {/* User Selection */}
                  <div style={{ marginBottom: '20px' }}>
                    <label style={{
                      display: 'block',
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#E5E7EB',
                      marginBottom: '8px'
                    }}>
                      Select User *
                    </label>
                    <input
                      type="text"
                      placeholder="Search by username, email, or ID..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '12px',
                        backgroundColor: '#374151',
                        border: '1px solid #4B5563',
                        borderRadius: '8px',
                        color: '#FFFFFF',
                        fontSize: '14px',
                        marginBottom: '8px'
                      }}
                    />
                    <select
                      value={withdrawalForm.userId}
                      onChange={(e) => setWithdrawalForm({ ...withdrawalForm, userId: e.target.value })}
                      required
                      style={{
                        width: '100%',
                        padding: '12px',
                        backgroundColor: '#374151',
                        border: '1px solid #4B5563',
                        borderRadius: '8px',
                        color: '#FFFFFF',
                        fontSize: '14px'
                      }}
                    >
                      <option value="">Select a user...</option>
                      {filteredUsers.map((user) => (
                        <option key={user.id} value={user.id}>
                          {user.username} - {user.full_name} ({user.email})
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Amount */}
                  <div style={{ marginBottom: '20px' }}>
                    <label style={{
                      display: 'block',
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#E5E7EB',
                      marginBottom: '8px'
                    }}>
                      Withdrawal Amount (USDT) *
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={withdrawalForm.amount}
                      onChange={(e) => setWithdrawalForm({ ...withdrawalForm, amount: e.target.value })}
                      required
                      style={{
                        width: '100%',
                        padding: '12px',
                        backgroundColor: '#374151',
                        border: '1px solid #4B5563',
                        borderRadius: '8px',
                        color: '#FFFFFF',
                        fontSize: '14px'
                      }}
                      placeholder="0.00"
                    />
                  </div>

                  {/* User Wallet Address (Destination) */}
                  <div style={{ marginBottom: '20px' }}>
                    <label style={{
                      display: 'block',
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#E5E7EB',
                      marginBottom: '8px'
                    }}>
                      User Wallet Address (Destination) *
                    </label>
                    <input
                      type="text"
                      value={withdrawalForm.userWalletAddress}
                      onChange={(e) => setWithdrawalForm({ ...withdrawalForm, userWalletAddress: e.target.value })}
                      required
                      style={{
                        width: '100%',
                        padding: '12px',
                        backgroundColor: '#374151',
                        border: '1px solid #4B5563',
                        borderRadius: '8px',
                        color: '#FFFFFF',
                        fontSize: '14px'
                      }}
                      placeholder="Enter user's wallet address where funds will be sent..."
                    />
                  </div>

                  {/* Company Wallet Address (Source) */}
                  <div style={{ marginBottom: '20px' }}>
                    <label style={{
                      display: 'block',
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#E5E7EB',
                      marginBottom: '8px'
                    }}>
                      Company Wallet Address (Source)
                    </label>
                    <input
                      type="text"
                      value={withdrawalForm.companyWalletAddress}
                      onChange={(e) => setWithdrawalForm({ ...withdrawalForm, companyWalletAddress: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '12px',
                        backgroundColor: '#374151',
                        border: '1px solid #4B5563',
                        borderRadius: '8px',
                        color: '#FFFFFF',
                        fontSize: '14px'
                      }}
                      placeholder="Enter company wallet address used to send funds..."
                    />
                  </div>

                  {/* Network Selection */}
                  <div style={{ marginBottom: '20px' }}>
                    <label style={{
                      display: 'block',
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#E5E7EB',
                      marginBottom: '8px'
                    }}>
                      Network *
                    </label>
                    <select
                      value={withdrawalForm.network}
                      onChange={(e) => setWithdrawalForm({ ...withdrawalForm, network: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '12px',
                        backgroundColor: '#374151',
                        border: '1px solid #4B5563',
                        borderRadius: '8px',
                        color: '#FFFFFF',
                        fontSize: '14px'
                      }}
                    >
                      <option value="USDT-BEP20">USDT-BEP20 (BSC)</option>
                      <option value="USDT-TRC20">USDT-TRC20 (Tron)</option>
                      <option value="USDT-ERC20">USDT-ERC20 (Ethereum)</option>
                      <option value="USDT-POL">USDT-POL (Polygon)</option>
                    </select>
                  </div>

                  {/* Transaction Hash */}
                  <div style={{ marginBottom: '20px' }}>
                    <label style={{
                      display: 'block',
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#E5E7EB',
                      marginBottom: '8px'
                    }}>
                      Transaction Hash
                    </label>
                    <input
                      type="text"
                      value={withdrawalForm.transactionHash}
                      onChange={(e) => setWithdrawalForm({ ...withdrawalForm, transactionHash: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '12px',
                        backgroundColor: '#374151',
                        border: '1px solid #4B5563',
                        borderRadius: '8px',
                        color: '#FFFFFF',
                        fontSize: '14px'
                      }}
                      placeholder="Enter blockchain transaction hash..."
                    />
                  </div>

                  {/* Proof of Payment Upload */}
                  <div style={{ marginBottom: '20px' }}>
                    <label style={{
                      display: 'block',
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#E5E7EB',
                      marginBottom: '8px'
                    }}>
                      Proof of Payment
                    </label>
                    <div style={{
                      border: '2px dashed #4B5563',
                      borderRadius: '8px',
                      padding: '20px',
                      textAlign: 'center',
                      backgroundColor: '#374151'
                    }}>
                      <input
                        type="file"
                        accept="image/*,.pdf"
                        onChange={async (e) => {
                          const file = e.target.files?.[0]
                          if (file) {
                            const url = await handleProofUpload(file)
                            if (url) {
                              setWithdrawalForm({ ...withdrawalForm, proofOfPayment: url })
                            }
                          }
                        }}
                        style={{
                          width: '100%',
                          padding: '8px',
                          backgroundColor: 'transparent',
                          border: 'none',
                          color: '#FFFFFF',
                          fontSize: '14px'
                        }}
                      />
                      {uploadingProof && (
                        <p style={{ color: '#F59E0B', fontSize: '14px', marginTop: '8px' }}>
                          Uploading proof of payment...
                        </p>
                      )}
                      {withdrawalForm.proofOfPayment && (
                        <div style={{ marginTop: '8px' }}>
                          <p style={{ color: '#10B981', fontSize: '14px' }}>
                            ✅ Proof uploaded successfully
                          </p>
                          <a
                            href={withdrawalForm.proofOfPayment}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              color: '#F59E0B',
                              fontSize: '14px',
                              textDecoration: 'underline'
                            }}
                          >
                            View uploaded proof
                          </a>
                        </div>
                      )}
                      <p style={{ color: '#ffffff', fontSize: '14px', marginTop: '8px' }}>
                        Upload screenshot or PDF of payment confirmation
                      </p>
                    </div>
                  </div>

                  {/* Status */}
                  <div style={{ marginBottom: '20px' }}>
                    <label style={{
                      display: 'block',
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#E5E7EB',
                      marginBottom: '8px'
                    }}>
                      Status *
                    </label>
                    <select
                      value={withdrawalForm.status}
                      onChange={(e) => setWithdrawalForm({ ...withdrawalForm, status: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '12px',
                        backgroundColor: '#374151',
                        border: '1px solid #4B5563',
                        borderRadius: '8px',
                        color: '#FFFFFF',
                        fontSize: '14px'
                      }}
                    >
                      <option value="pending">Pending</option>
                      <option value="approved">Approved / Processing</option>
                      <option value="completed">Completed</option>
                      <option value="rejected">Rejected</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>

                  {/* Request Date */}
                  <div style={{ marginBottom: '20px' }}>
                    <label style={{
                      display: 'block',
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#E5E7EB',
                      marginBottom: '8px'
                    }}>
                      Request Date *
                    </label>
                    <input
                      type="date"
                      value={withdrawalForm.requestDate}
                      onChange={(e) => setWithdrawalForm({ ...withdrawalForm, requestDate: e.target.value })}
                      required
                      style={{
                        width: '100%',
                        padding: '12px',
                        backgroundColor: '#374151',
                        border: '1px solid #4B5563',
                        borderRadius: '8px',
                        color: '#FFFFFF',
                        fontSize: '14px'
                      }}
                    />
                  </div>

                  {/* Processing Date */}
                  <div style={{ marginBottom: '20px' }}>
                    <label style={{
                      display: 'block',
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#E5E7EB',
                      marginBottom: '8px'
                    }}>
                      Processing Date (Optional)
                    </label>
                    <input
                      type="date"
                      value={withdrawalForm.processedDate}
                      onChange={(e) => setWithdrawalForm({ ...withdrawalForm, processedDate: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '12px',
                        backgroundColor: '#374151',
                        border: '1px solid #4B5563',
                        borderRadius: '8px',
                        color: '#FFFFFF',
                        fontSize: '14px'
                      }}
                    />
                  </div>

                  {/* Admin Notes */}
                  <div style={{ marginBottom: '24px' }}>
                    <label style={{
                      display: 'block',
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#E5E7EB',
                      marginBottom: '8px'
                    }}>
                      Admin Notes
                    </label>
                    <textarea
                      value={withdrawalForm.adminNotes}
                      onChange={(e) => setWithdrawalForm({ ...withdrawalForm, adminNotes: e.target.value })}
                      rows={3}
                      style={{
                        width: '100%',
                        padding: '12px',
                        backgroundColor: '#374151',
                        border: '1px solid #4B5563',
                        borderRadius: '8px',
                        color: '#FFFFFF',
                        fontSize: '14px',
                        resize: 'vertical'
                      }}
                      placeholder="Internal notes about this withdrawal..."
                    />
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={loading}
                    style={{
                      width: '100%',
                      padding: '14px',
                      backgroundColor: '#F59E0B',
                      color: '#000000',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '16px',
                      fontWeight: '600',
                      cursor: loading ? 'not-allowed' : 'pointer',
                      opacity: loading ? 0.7 : 1,
                      transition: 'all 0.2s ease'
                    }}
                  >
                    {loading ? 'Processing...' : '🏦 Create Withdrawal'}
                  </button>
                </form>
              </div>
            )}
          </div>

          {/* Sidebar - Recent Records */}
          <div style={{
            backgroundColor: '#1F2937',
            borderRadius: '12px',
            padding: '24px',
            border: '1px solid #374151'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px'
            }}>
              <h3 style={{
                fontSize: '18px',
                fontWeight: '600',
                color: '#F59E0B',
                margin: 0
              }}>
                📊 Recent {activeTab === 'commission' ? 'Commissions' : 'Withdrawals'}
              </h3>
              <button
                onClick={() => setShowExporter({ show: true, type: activeTab === 'commission' || activeTab === 'withdrawal' ? activeTab : 'commission' })}
                style={{
                  padding: '8px 12px',
                  backgroundColor: '#374151',
                  color: '#F59E0B',
                  border: '1px solid #4B5563',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = '#4B5563'
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = '#374151'
                }}
              >
                📊 Export
              </button>
            </div>

            <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
              {recentRecords.length === 0 ? (
                <p style={{ color: '#ffffff', textAlign: 'center', padding: '20px' }}>
                  No recent records found
                </p>
              ) : (
                recentRecords.map((record, _index) => (
                  <div
                    key={record.id}
                    style={{
                      backgroundColor: '#374151',
                      borderRadius: '8px',
                      padding: '16px',
                      marginBottom: '12px',
                      border: '1px solid #4B5563'
                    }}
                  >
                    {activeTab === 'commission' ? (
                      <div>
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginBottom: '8px'
                        }}>
                          <span style={{
                            color: '#10B981',
                            fontWeight: '600',
                            fontSize: '16px'
                          }}>
                            +${record.usdt_commission?.toFixed(2) || '0.00'}
                          </span>
                          <span style={{
                            backgroundColor: record.status === 'approved' ? '#10B981' : '#F59E0B',
                            color: '#000000',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontSize: '14px',
                            fontWeight: '600'
                          }}>
                            {record.status}
                          </span>
                        </div>
                        <p style={{ color: '#E5E7EB', fontSize: '14px', marginBottom: '4px' }}>
                          User: {record.referrer?.username || 'Unknown'}
                        </p>
                        <p style={{ color: '#ffffff', fontSize: '14px' }}>
                          {new Date(record.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    ) : (
                      <div>
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginBottom: '8px'
                        }}>
                          <span style={{
                            color: '#EF4444',
                            fontWeight: '600',
                            fontSize: '16px'
                          }}>
                            -${record.withdrawal_amount?.toFixed(2) || '0.00'}
                          </span>
                          <span style={{
                            backgroundColor:
                              record.status === 'completed' ? '#10B981' :
                              record.status === 'processing' ? '#F59E0B' :
                              record.status === 'failed' ? '#EF4444' : '#6B7280',
                            color: '#000000',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontSize: '14px',
                            fontWeight: '600'
                          }}>
                            {record.status}
                          </span>
                        </div>
                        <p style={{ color: '#E5E7EB', fontSize: '14px', marginBottom: '4px' }}>
                          User: {record.user?.username || 'Unknown'}
                        </p>
                        <p style={{ color: '#ffffff', fontSize: '14px' }}>
                          {new Date(record.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Confirmation Dialog */}
        {showConfirmDialog.show && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{
              backgroundColor: '#1F2937',
              borderRadius: '12px',
              padding: '32px',
              border: '1px solid #374151',
              maxWidth: '500px',
              width: '90%'
            }}>
              <h3 style={{
                fontSize: '20px',
                fontWeight: '600',
                color: '#F59E0B',
                marginBottom: '16px'
              }}>
                {showConfirmDialog.type === 'commission' ? '💸 Confirm Commission Addition' : '🏦 Confirm Withdrawal Creation'}
              </h3>

              <div style={{
                backgroundColor: '#374151',
                borderRadius: '8px',
                padding: '16px',
                marginBottom: '24px'
              }}>
                {showConfirmDialog.type === 'commission' ? (
                  <div>
                    <p style={{ color: '#E5E7EB', marginBottom: '8px' }}>
                      <strong>User:</strong> {users.find(u => u.id.toString() === commissionForm.userId)?.username}
                    </p>
                    <p style={{ color: '#E5E7EB', marginBottom: '8px' }}>
                      <strong>Type:</strong> {commissionForm.type}
                    </p>
                    <p style={{ color: '#E5E7EB', marginBottom: '8px' }}>
                      <strong>Amount:</strong> ${commissionForm.amount} USDT
                    </p>
                    <p style={{ color: '#E5E7EB', marginBottom: '8px' }}>
                      <strong>Description:</strong> {commissionForm.description}
                    </p>
                    <p style={{ color: '#E5E7EB' }}>
                      <strong>Date:</strong> {commissionForm.date}
                    </p>
                  </div>
                ) : (
                  <div>
                    <p style={{ color: '#E5E7EB', marginBottom: '8px' }}>
                      <strong>User:</strong> {users.find(u => u.id.toString() === withdrawalForm.userId)?.username}
                    </p>
                    <p style={{ color: '#E5E7EB', marginBottom: '8px' }}>
                      <strong>Amount:</strong> ${withdrawalForm.amount} USDT
                    </p>
                    <p style={{ color: '#E5E7EB', marginBottom: '8px' }}>
                      <strong>Network:</strong> {withdrawalForm.network}
                    </p>
                    <p style={{ color: '#E5E7EB', marginBottom: '8px' }}>
                      <strong>User Wallet:</strong> {withdrawalForm.userWalletAddress}
                    </p>
                    {withdrawalForm.companyWalletAddress && (
                      <p style={{ color: '#E5E7EB', marginBottom: '8px' }}>
                        <strong>Company Wallet:</strong> {withdrawalForm.companyWalletAddress}
                      </p>
                    )}
                    {withdrawalForm.transactionHash && (
                      <p style={{ color: '#E5E7EB', marginBottom: '8px' }}>
                        <strong>Transaction Hash:</strong> {withdrawalForm.transactionHash.substring(0, 20)}...
                      </p>
                    )}
                    {withdrawalForm.proofOfPayment && (
                      <p style={{ color: '#E5E7EB', marginBottom: '8px' }}>
                        <strong>Proof of Payment:</strong> <span style={{ color: '#10B981' }}>✅ Uploaded</span>
                      </p>
                    )}
                    <p style={{ color: '#E5E7EB', marginBottom: '8px' }}>
                      <strong>Status:</strong> {withdrawalForm.status}
                    </p>
                    <p style={{ color: '#E5E7EB' }}>
                      <strong>Request Date:</strong> {withdrawalForm.requestDate}
                    </p>
                  </div>
                )}
              </div>

              {/* v4.7.71: step-up TOTP — only for commission additions. The
                  master password remains required (entered on the form above);
                  this is an additional factor that proves possession of the
                  admin's authenticator at the moment of the write. */}
              {showConfirmDialog.type === 'commission' && (
                <div style={{ marginBottom: '20px' }}>
                  <label style={{
                    display: 'block',
                    color: '#E5E7EB',
                    fontSize: '14px',
                    fontWeight: '600',
                    marginBottom: '8px'
                  }}>
                    🔑 Authenticator Code <span style={{ color: '#EF4444' }}>*</span>
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    value={commissionTotpCode}
                    onChange={(e) => {
                      const v = e.target.value.replace(/\D/g, '').slice(0, 8)
                      setCommissionTotpCode(v)
                      if (commissionMfaError) setCommissionMfaError(null)
                    }}
                    placeholder="6-digit code (or 8-digit backup code)"
                    autoFocus
                    disabled={loading}
                    style={{
                      width: '100%',
                      padding: '12px',
                      backgroundColor: '#111827',
                      color: '#FFFFFF',
                      border: `1px solid ${commissionMfaError ? '#EF4444' : '#4B5563'}`,
                      borderRadius: '8px',
                      fontSize: '18px',
                      letterSpacing: '4px',
                      textAlign: 'center',
                      fontFamily: 'monospace'
                    }}
                  />
                  {commissionMfaError && (
                    <p style={{ color: '#EF4444', fontSize: '13px', marginTop: '6px' }}>
                      {commissionMfaError}
                    </p>
                  )}
                  <p style={{ color: '#9CA3AF', fontSize: '12px', marginTop: '6px' }}>
                    Open your authenticator app and enter the current 6-digit code, or paste an 8-digit backup code.
                  </p>
                </div>
              )}

              <div style={{
                display: 'flex',
                gap: '12px',
                justifyContent: 'flex-end'
              }}>
                <button
                  onClick={() => {
                    setShowConfirmDialog({ show: false, type: 'commission' })
                    setCommissionTotpCode('')
                    setCommissionMfaError(null)
                  }}
                  disabled={loading}
                  style={{
                    padding: '12px 24px',
                    backgroundColor: '#ffffff',
                    color: '#FFFFFF',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    opacity: loading ? 0.7 : 1
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={confirmAction}
                  disabled={loading}
                  style={{
                    padding: '12px 24px',
                    backgroundColor: '#F59E0B',
                    color: '#000000',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    opacity: loading ? 0.7 : 1
                  }}
                >
                  {loading ? 'Processing...' : 'Confirm'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Export Dialog */}
        {showExporter.show && (
          <RecordExporter
            type={showExporter.type}
            onClose={() => setShowExporter({ show: false, type: 'commission' })}
          />
        )}

        {/* Approval Modal */}
        {showApprovalModal && selectedWithdrawal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{
              backgroundColor: '#1F2937',
              borderRadius: '12px',
              padding: '32px',
              maxWidth: '500px',
              width: '90%',
              border: '1px solid #374151'
            }}>
              <h3 style={{
                fontSize: '20px',
                fontWeight: '600',
                color: '#F59E0B',
                marginBottom: '24px',
                textAlign: 'center'
              }}>
                ✅ Approve Withdrawal
              </h3>

              <div style={{
                backgroundColor: '#111827',
                padding: '16px',
                borderRadius: '8px',
                marginBottom: '24px',
                border: '1px solid #374151'
              }}>
                <h4 style={{ color: '#ffffff', marginBottom: '12px', fontSize: '16px' }}>Withdrawal Details:</h4>
                <p style={{ color: '#ffffff', margin: '4px 0' }}>User: {selectedWithdrawal.users?.username} (ID: {selectedWithdrawal.user_id})</p>
                <p style={{ color: '#ffffff', margin: '4px 0' }}>Amount: ${selectedWithdrawal.withdrawal_amount?.toFixed(2)} USDT</p>
                <p style={{ color: '#ffffff', margin: '4px 0' }}>Wallet: {selectedWithdrawal.wallet_address}</p>
                <p style={{ color: '#ffffff', margin: '4px 0' }}>Network: {selectedWithdrawal.network}</p>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#ffffff',
                  marginBottom: '8px'
                }}>
                  Proof of Payment *
                </label>
                <input
                  type="file"
                  accept="image/*,.pdf"
                  onChange={(e) => setProofFile(e.target.files?.[0] || null)}
                  style={{
                    width: '100%',
                    padding: '12px',
                    backgroundColor: '#374151',
                    border: '1px solid #4B5563',
                    borderRadius: '6px',
                    color: '#ffffff',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#ffffff',
                  marginBottom: '8px'
                }}>
                  Transaction Hash *
                </label>
                <input
                  type="text"
                  value={transactionHash}
                  onChange={(e) => setTransactionHash(e.target.value)}
                  placeholder="Enter blockchain transaction hash"
                  style={{
                    width: '100%',
                    padding: '12px',
                    backgroundColor: '#374151',
                    border: '1px solid #4B5563',
                    borderRadius: '6px',
                    color: '#ffffff',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => {
                    setShowApprovalModal(false)
                    setSelectedWithdrawal(null)
                    setProofFile(null)
                    setTransactionHash('')
                  }}
                  disabled={processingAction}
                  style={{
                    padding: '12px 24px',
                    backgroundColor: '#374151',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: processingAction ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    opacity: processingAction ? 0.7 : 1
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleApproveWithdrawal}
                  disabled={processingAction || !proofFile || !transactionHash.trim()}
                  style={{
                    padding: '12px 24px',
                    backgroundColor: '#10B981',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: (processingAction || !proofFile || !transactionHash.trim()) ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    opacity: (processingAction || !proofFile || !transactionHash.trim()) ? 0.7 : 1
                  }}
                >
                  {processingAction ? 'Processing...' : '✅ Approve & Send'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Rejection Modal */}
        {showRejectionModal && selectedWithdrawal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{
              backgroundColor: '#1F2937',
              borderRadius: '12px',
              padding: '32px',
              maxWidth: '500px',
              width: '90%',
              border: '1px solid #374151'
            }}>
              <h3 style={{
                fontSize: '20px',
                fontWeight: '600',
                color: '#EF4444',
                marginBottom: '24px',
                textAlign: 'center'
              }}>
                ❌ Reject Withdrawal
              </h3>

              <div style={{
                backgroundColor: '#111827',
                padding: '16px',
                borderRadius: '8px',
                marginBottom: '24px',
                border: '1px solid #374151'
              }}>
                <h4 style={{ color: '#ffffff', marginBottom: '12px', fontSize: '16px' }}>Withdrawal Details:</h4>
                <p style={{ color: '#ffffff', margin: '4px 0' }}>User: {selectedWithdrawal.users?.username} (ID: {selectedWithdrawal.user_id})</p>
                <p style={{ color: '#ffffff', margin: '4px 0' }}>Amount: ${selectedWithdrawal.withdrawal_amount?.toFixed(2)} USDT</p>
                <p style={{ color: '#ffffff', margin: '4px 0' }}>Wallet: {selectedWithdrawal.wallet_address}</p>
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#ffffff',
                  marginBottom: '8px'
                }}>
                  Rejection Reason *
                </label>
                <textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Please provide a clear reason for rejection..."
                  rows={4}
                  style={{
                    width: '100%',
                    padding: '12px',
                    backgroundColor: '#374151',
                    border: '1px solid #4B5563',
                    borderRadius: '6px',
                    color: '#ffffff',
                    fontSize: '14px',
                    resize: 'vertical'
                  }}
                />
              </div>

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => {
                    setShowRejectionModal(false)
                    setSelectedWithdrawal(null)
                    setRejectionReason('')
                  }}
                  disabled={processingAction}
                  style={{
                    padding: '12px 24px',
                    backgroundColor: '#374151',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: processingAction ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    opacity: processingAction ? 0.7 : 1
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleRejectWithdrawal}
                  disabled={processingAction || !rejectionReason.trim()}
                  style={{
                    padding: '12px 24px',
                    backgroundColor: '#EF4444',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: (processingAction || !rejectionReason.trim()) ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    opacity: (processingAction || !rejectionReason.trim()) ? 0.7 : 1
                  }}
                >
                  {processingAction ? 'Processing...' : '❌ Reject Withdrawal'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default CommissionWithdrawalManager
